import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Copy, RefreshCw, Save, Server, Zap, Activity, Mail, AlertTriangle, Settings as SettingsIcon, Edit3, Plus, Trash2, Play, Send, ShieldCheck, ShieldAlert, XCircle, CheckCircle2, Lightbulb } from "lucide-react";
import { api, formatApiErrorDetail } from "../../api";
import { Btn } from "../../components/common";
import { Modal, Field, Selecter, fmtDT } from "../crm/crmui";

const SUB_TABS = [
  { key: "senders", label: "Remetentes", icon: Server },
  { key: "defaults", label: "Preferencias globais", icon: SettingsIcon },
  { key: "logs", label: "Logs de envios", icon: Activity },
  { key: "webhooks", label: "Webhooks SendGrid", icon: Zap },
  { key: "botfilter", label: "Filtro de bots", icon: ShieldAlert },
];

const FINALIDADE_OPTIONS = [
  { value: "marketing", label: "Marketing (campanhas por plataforma)" },
  { value: "sistema", label: "Sistema (convites, reset password, alertas)" },
  { value: "crm", label: "CRM (emails transacionais das fichas)" },
];

const EVENT_BADGE = {
  open: "bg-emerald-100 text-emerald-800",
  click: "bg-blue-100 text-blue-800",
  delivered: "bg-slate-200 text-slate-700",
  bounce: "bg-red-100 text-red-800",
  dropped: "bg-red-100 text-red-800",
  unsubscribe: "bg-amber-100 text-amber-800",
  spamreport: "bg-rose-100 text-rose-800",
  processed: "bg-slate-100 text-slate-600",
};

export default function EmailMarketingTab() {
  const [sub, setSub] = useState("senders");
  return (
    <div className="space-y-4">
      <div className="bg-slate-50 border border-tint rounded-md p-3 text-[12px] text-navy flex items-start gap-2">
        <Mail size={14} className="mt-0.5 text-blue flex-shrink-0" />
        <div>
          <b>Centro de Email Marketing.</b> As campanhas sao sempre enviadas por um remetente (sender) especifico com a sua propria chave SendGrid e webhook. Nao existe chave global obrigatoria. Configure cada remetente abaixo e cole o seu URL de webhook unico no painel do SendGrid correspondente.
        </div>
      </div>

      <div className="border-b border-tint flex gap-1 overflow-x-auto">
        {SUB_TABS.map((t) => (
          <button key={t.key} onClick={() => setSub(t.key)}
            data-testid={`em-sub-${t.key}`}
            className={`text-xs px-4 py-2 border-b-2 inline-flex items-center gap-1.5 -mb-px whitespace-nowrap ${sub === t.key ? "border-blue text-navy font-semibold" : "border-transparent text-slate-500 hover:text-navy"}`}>
            <t.icon size={13} />{t.label}
          </button>
        ))}
      </div>

      {sub === "senders" && <SendersSection />}
      {sub === "defaults" && <DefaultsSection />}
      {sub === "logs" && <SendsLogSection />}
      {sub === "webhooks" && <WebhookLogSection />}
      {sub === "botfilter" && <BotFilterSection />}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Remetentes (lista + webhook URL por remetente)
// -----------------------------------------------------------------------------
function SendersSection() {
  const [senders, setSenders] = useState([]);
  const [edit, setEdit] = useState(null);
  const [testSender, setTestSender] = useState(null);
  const [domainAuth, setDomainAuth] = useState(null); // {sender_id, sender_nome, from_email, data, loading}
  const [loading, setLoading] = useState(false);
  const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/marketing/senders");
      setSenders(data || []);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const webhookUrl = (s) => `${base}/api/marketing/webhook/sendgrid/${s.id}?secret=${s.webhook_secret || "SEU_SECRET"}`;

  const copy = (txt) => {
    navigator.clipboard?.writeText(txt);
    toast.success("URL copiado.");
  };

  const save = async () => {
    try {
      const payload = {
        nome: edit.nome, from_email: edit.from_email, from_name: edit.from_name,
        reply_to: edit.reply_to || "", plataforma: edit.plataforma || "",
        finalidade: edit.finalidade || "marketing",
        sendgrid_api_key: edit.api_key_new || "",
        webhook_secret: edit.webhook_secret_new || "",
      };
      if (edit.id) {
        await api.put(`/marketing/senders/${edit.id}`, payload);
      } else {
        await api.post("/marketing/senders", payload);
      }
      toast.success("Remetente guardado.");
      setEdit(null);
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const remove = async (s) => {
    if (!confirm(`Eliminar remetente "${s.nome}"?`)) return;
    try {
      await api.delete(`/marketing/senders/${s.id}`);
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const enviarTeste = async () => {
    if (!testSender?.to) return toast.error("Indique um email de destino.");
    try {
      await api.post(`/marketing/senders/${testSender.sender.id}/test`, { to: testSender.to });
      toast.success(`Email de teste enviado para ${testSender.to}`);
      setTestSender(null);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const verificarDNS = async (sender) => {
    setDomainAuth({ sender_id: sender.id, sender_nome: sender.nome, from_email: sender.from_email, data: null, loading: true });
    try {
      const { data } = await api.get(`/marketing/senders/${sender.id}/domain-auth`);
      setDomainAuth((d) => ({ ...d, data, loading: false }));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Falha na verificacao DNS.");
      setDomainAuth(null);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted2">Cada remetente tem a sua propria chave SendGrid e webhook dedicado. Permite granularidade total: aberturas/cliques por campanha-remetente.</div>
        <Btn onClick={() => setEdit({ nome: "", from_email: "", from_name: "", finalidade: "marketing" })} testid="em-add-sender">
          <Plus size={13} /> Novo remetente
        </Btn>
      </div>

      {loading ? <div className="text-center py-6 text-muted2">A carregar...</div> :
       senders.length === 0 ? <div className="border border-dashed border-tint rounded p-6 text-center text-muted2">Sem remetentes. Crie o primeiro acima.</div> :
       <div className="space-y-3">
         {senders.map((s) => (
           <div key={s.id} className="border border-tint rounded-lg bg-white p-3" data-testid={`em-sender-${s.id}`}>
             <div className="flex items-start justify-between gap-3">
               <div className="flex-1 min-w-0">
                 <div className="flex items-center gap-2 mb-1">
                   <div className="font-semibold text-navy">{s.nome}</div>
                   <span className={`text-[10px] px-2 py-0.5 rounded font-bold ${s.sendgrid_api_key_configurado ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-800"}`}>
                     {s.sendgrid_api_key_configurado ? "API KEY OK" : "SEM API KEY"}
                   </span>
                   <span className={`text-[10px] px-2 py-0.5 rounded font-bold ${s.webhook_secret_configurado ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
                     {s.webhook_secret_configurado ? "WEBHOOK OK" : "SEM WEBHOOK SECRET"}
                   </span>
                   {s.finalidade && <span className="text-[10px] px-2 py-0.5 rounded bg-slate-200 text-slate-700 font-bold">{s.finalidade.toUpperCase()}</span>}
                   {s.plataforma && <span className="text-[10px] px-2 py-0.5 rounded bg-blue-100 text-blue-800 font-bold">{s.plataforma}</span>}
                 </div>
                 <div className="text-[12px] text-muted2 truncate">
                   {s.from_name} &lt;{s.from_email}&gt;{s.reply_to ? ` -- reply-to: ${s.reply_to}` : ""}
                 </div>
                 <div className="mt-2 p-2 bg-slate-50 border border-tint rounded flex items-center gap-2">
                   <code className="flex-1 text-[11px] font-mono break-all">{webhookUrl(s)}</code>
                   <button onClick={() => copy(webhookUrl(s))} className="text-blue hover:underline text-[11px] whitespace-nowrap inline-flex items-center gap-1"
                     data-testid={`em-sender-copy-${s.id}`}>
                     <Copy size={11} /> Copiar
                   </button>
                 </div>
                 <div className="text-[10px] text-muted2 mt-1">
                   Cole este URL em SendGrid Dashboard &rarr; Settings &rarr; Mail Settings &rarr; Event Webhook deste remetente. Active: opened, clicked, bounce, unsubscribe, spamreport, delivered.
                 </div>
               </div>
               <div className="flex flex-col gap-1">
                 <button onClick={() => setEdit({ ...s, api_key_new: "", webhook_secret_new: "" })}
                   data-testid={`em-sender-edit-${s.id}`}
                   className="text-[11px] px-2 py-1 border border-tint rounded hover:bg-slate-50 inline-flex items-center gap-1">
                   <Edit3 size={11} /> Editar
                 </button>
                 <button onClick={() => setTestSender({ sender: s, to: "" })} disabled={!s.sendgrid_api_key_configurado}
                   data-testid={`em-sender-test-${s.id}`}
                   className="text-[11px] px-2 py-1 border border-tint rounded hover:bg-slate-50 inline-flex items-center gap-1 disabled:opacity-50">
                   <Play size={11} /> Testar
                 </button>
                 <button onClick={() => verificarDNS(s)} data-testid={`em-sender-dns-${s.id}`}
                   className="text-[11px] px-2 py-1 border border-tint rounded hover:bg-slate-50 inline-flex items-center gap-1">
                   <ShieldCheck size={11} /> Verificar DNS
                 </button>
                 <button onClick={() => remove(s)} className="p-1.5 text-red-500 hover:bg-red-50 rounded self-end"><Trash2 size={12} /></button>
               </div>
             </div>
           </div>
         ))}
       </div>}

      <Modal open={!!edit} onClose={() => setEdit(null)} title={edit?.id ? "Editar remetente" : "Novo remetente"} testid="em-sender-modal" size="lg">
        {edit && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Nome interno" value={edit.nome || ""} onChange={(v) => setEdit({ ...edit, nome: v })} testid="em-sender-nome" />
              <Selecter label="Finalidade" value={edit.finalidade || "marketing"} onChange={(v) => setEdit({ ...edit, finalidade: v })}
                options={FINALIDADE_OPTIONS} testid="em-sender-fin" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="From email" value={edit.from_email || ""} onChange={(v) => setEdit({ ...edit, from_email: v })} testid="em-sender-from" />
              <Field label="From nome" value={edit.from_name || ""} onChange={(v) => setEdit({ ...edit, from_name: v })} testid="em-sender-fromname" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Reply-to" value={edit.reply_to || ""} onChange={(v) => setEdit({ ...edit, reply_to: v })} testid="em-sender-reply" />
              <Field label="Plataforma (opcional)" value={edit.plataforma || ""} onChange={(v) => setEdit({ ...edit, plataforma: v })} testid="em-sender-plat" />
            </div>
            <Field label={edit.sendgrid_api_key_configurado ? "Nova API key SendGrid (deixa vazio para manter)" : "API key SendGrid"} type="password"
              value={edit.api_key_new || ""} onChange={(v) => setEdit({ ...edit, api_key_new: v })} testid="em-sender-apikey" />
            <Field label={edit.webhook_secret_configurado ? "Novo webhook secret (deixa vazio para manter)" : "Webhook secret (define 1 valor seguro aqui e cola o mesmo no SendGrid Event Webhook)"} type="password"
              value={edit.webhook_secret_new || ""} onChange={(v) => setEdit({ ...edit, webhook_secret_new: v })} testid="em-sender-secret" />
            <div className="flex justify-end gap-2">
              <Btn variant="ghost" onClick={() => setEdit(null)}>Cancelar</Btn>
              <Btn onClick={save} testid="em-sender-save"><Save size={13} /> Guardar</Btn>
            </div>
          </div>
        )}
      </Modal>

      <Modal open={!!testSender} onClose={() => setTestSender(null)} title="Enviar email de teste" testid="em-sender-test-modal">
        {testSender && (
          <>
            <div className="text-sm mb-2">Remetente: <strong>{testSender.sender.nome}</strong> ({testSender.sender.from_email})</div>
            <Field label="Email de destino" value={testSender.to} onChange={(v) => setTestSender({ ...testSender, to: v })} testid="em-sender-test-to" />
            <div className="flex justify-end gap-2 pt-2">
              <Btn variant="ghost" onClick={() => setTestSender(null)}>Cancelar</Btn>
              <Btn onClick={enviarTeste} testid="em-sender-test-send"><Send size={14} /> Enviar teste</Btn>
            </div>
          </>
        )}
      </Modal>

      <Modal open={!!domainAuth} onClose={() => setDomainAuth(null)} title="Autenticacao de dominio (SPF/DKIM/DMARC)" testid="em-sender-dns-modal" size="lg">
        {domainAuth && (
          <DomainAuthPanel state={domainAuth} onRefresh={() => verificarDNS({ id: domainAuth.sender_id, nome: domainAuth.sender_nome, from_email: domainAuth.from_email })} />
        )}
      </Modal>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Preferencias globais (opens/clicks default, alerts, digest)
// -----------------------------------------------------------------------------
function DefaultsSection() {
  const [settings, setSettings] = useState(null);
  const load = useCallback(async () => {
    try { const { data } = await api.get("/marketing/settings"); setSettings(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      await api.put("/marketing/settings", {
        from_email: settings.from_email, from_name: settings.from_name, reply_to: settings.reply_to || "",
        track_opens: !!settings.track_opens, track_clicks: !!settings.track_clicks,
        unsubscribe_url_base: settings.unsubscribe_url_base || "",
        alerts_email: settings.alerts_email || "",
        digest_email: settings.digest_email || "",
        digest_frequencia: settings.digest_frequencia || "semanal",
        digest_dia_semana: settings.digest_dia_semana ?? 1,
      });
      toast.success("Preferencias guardadas.");
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (!settings) return <div className="text-center py-6 text-muted2">A carregar...</div>;

  return (
    <div className="max-w-2xl space-y-4">
      <div className="border border-amber-300 bg-amber-50 rounded p-3 text-[12px] text-amber-900 flex items-start gap-2">
        <AlertTriangle size={14} className="mt-0.5" />
        <div>A chave global SendGrid foi <b>depreciada</b>. Cada remetente deve ter a sua propria chave (ver separador "Remetentes"). As preferencias abaixo sao predefinicoes aplicadas a novas campanhas que nao sobrepoem explicitamente.</div>
      </div>

      <div className="border border-tint rounded-lg p-4 bg-white space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Field label="From email (fallback)" value={settings.from_email || ""} onChange={(v) => setSettings({ ...settings, from_email: v })} testid="em-def-from" />
          <Field label="From nome (fallback)" value={settings.from_name || ""} onChange={(v) => setSettings({ ...settings, from_name: v })} testid="em-def-fromname" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Reply-to (fallback)" value={settings.reply_to || ""} onChange={(v) => setSettings({ ...settings, reply_to: v })} testid="em-def-reply" />
          <Field label="Base URL unsubscribe (opcional)" value={settings.unsubscribe_url_base || ""} onChange={(v) => setSettings({ ...settings, unsubscribe_url_base: v })} testid="em-def-unsub" />
        </div>
        <div className="flex gap-4 pt-2 border-t border-tint">
          <label className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={!!settings.track_opens} onChange={(e) => setSettings({ ...settings, track_opens: e.target.checked })} data-testid="em-def-opens" /> Track opens (predefinicao)</label>
          <label className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={!!settings.track_clicks} onChange={(e) => setSettings({ ...settings, track_clicks: e.target.checked })} data-testid="em-def-clicks" /> Track clicks (predefinicao)</label>
        </div>
        <div className="pt-2 border-t border-tint">
          <div className="text-sm font-semibold text-navy mb-1">Alertas por email</div>
          <div className="text-[11px] text-muted2 mb-2">
            Emails que recebem alertas quando um remetente entra em estado critico. Separe multiplos por virgula.
          </div>
          <Field label="Destinatarios (csv)" value={settings.alerts_email || ""} onChange={(v) => setSettings({ ...settings, alerts_email: v })} testid="em-def-alerts" />
        </div>
        <div className="pt-2 border-t border-tint">
          <div className="text-sm font-semibold text-navy mb-1">Digest executivo</div>
          <div className="text-[11px] text-muted2 mb-2">
            Resumo periodico de saude: bounce por marca, campanhas com pior performance, remetentes em risco. Vazio = fallback para alerts_email.
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Destinatarios (csv)" value={settings.digest_email || ""} onChange={(v) => setSettings({ ...settings, digest_email: v })} testid="em-def-digest" />
            <div>
              <label className="block text-sm font-semibold text-navy mb-1">Frequencia</label>
              <select value={settings.digest_frequencia || "semanal"} onChange={(e) => setSettings({ ...settings, digest_frequencia: e.target.value })}
                data-testid="em-def-digest-freq" className="w-full px-3 py-2 border border-tint rounded text-sm">
                <option value="semanal">Semanal</option><option value="diario">Diario</option><option value="off">Desactivado</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-navy mb-1">Dia</label>
              <select value={settings.digest_dia_semana ?? 1} onChange={(e) => setSettings({ ...settings, digest_dia_semana: parseInt(e.target.value) })}
                data-testid="em-def-digest-dia" disabled={settings.digest_frequencia !== "semanal"}
                className="w-full px-3 py-2 border border-tint rounded text-sm disabled:opacity-50">
                {["Segunda", "Terca", "Quarta", "Quinta", "Sexta", "Sabado", "Domingo"].map((d, i) => <option key={i} value={i}>{d}</option>)}
              </select>
            </div>
          </div>
        </div>
        <div className="flex justify-end">
          <Btn onClick={save} testid="em-def-save"><Save size={13} /> Guardar</Btn>
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Logs de envios (todos os remetentes/campanhas)
// -----------------------------------------------------------------------------
function SendsLogSection() {
  const [rows, setRows] = useState([]);
  const [senders, setSenders] = useState([]);
  const [filters, setFilters] = useState({ sender_id: "", estado: "", limit: 200 });
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
      const { data } = await api.get(`/marketing/sends-log?${params.toString()}`);
      setRows(data || []);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [filters]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/marketing/senders").then(({ data }) => setSenders(data || [])).catch(() => {});
  }, []);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <select value={filters.sender_id} onChange={(e) => setFilters({ ...filters, sender_id: e.target.value })}
          data-testid="em-log-filter-sender"
          className="text-[12px] border border-tint rounded px-2 py-1 bg-white">
          <option value="">Todos os remetentes</option>
          {senders.map((s) => <option key={s.id} value={s.id}>{s.nome}</option>)}
        </select>
        <select value={filters.estado} onChange={(e) => setFilters({ ...filters, estado: e.target.value })}
          data-testid="em-log-filter-estado"
          className="text-[12px] border border-tint rounded px-2 py-1 bg-white">
          <option value="">Qualquer estado</option>
          <option value="enviado">Enviado</option>
          <option value="erro">Erro</option>
          <option value="bounced">Bounced</option>
        </select>
        <select value={filters.limit} onChange={(e) => setFilters({ ...filters, limit: Number(e.target.value) })}
          data-testid="em-log-filter-limit"
          className="text-[12px] border border-tint rounded px-2 py-1 bg-white">
          {[100, 200, 500, 1000].map((n) => <option key={n} value={n}>Ultimos {n}</option>)}
        </select>
        <button onClick={load} data-testid="em-log-refresh"
          className="text-[12px] text-blue hover:underline inline-flex items-center gap-1">
          <RefreshCw size={11} /> Actualizar
        </button>
        <div className="ml-auto text-[11px] text-muted2">{rows.length} registos</div>
      </div>

      <div className="border border-tint rounded-lg bg-white overflow-hidden">
        {loading ? <div className="text-center py-6 text-muted2">A carregar...</div> :
         rows.length === 0 ? <div className="text-center py-8 text-muted2 italic">Sem envios registados para os filtros seleccionados.</div> :
         <div className="overflow-x-auto">
           <table className="w-full text-[12px]" data-testid="em-log-table">
             <thead className="text-[10px] uppercase text-slate-500 border-b border-tint bg-slate-50">
               <tr>
                 <th className="text-left p-2">Enviado</th>
                 <th className="text-left p-2">Email</th>
                 <th className="text-left p-2">Campanha</th>
                 <th className="text-left p-2">Remetente</th>
                 <th className="text-left p-2 w-20">Estado</th>
                 <th className="text-left p-2">Erro</th>
                 <th className="text-center p-2 w-14">Abriu</th>
                 <th className="text-center p-2 w-14">Click</th>
               </tr>
             </thead>
             <tbody>
               {rows.map((r) => (
                 <tr key={r.id} className="border-b border-tint last:border-b-0" data-testid={`em-log-row-${r.id}`}>
                   <td className="p-2 text-[11px] text-muted2">{fmtDT(r.sent_at)}</td>
                   <td className="p-2 truncate max-w-xs">{r.email}</td>
                   <td className="p-2 truncate max-w-xs text-navy">{r.campaign_nome || r.campaign_id || "-"}</td>
                   <td className="p-2 text-[11px]">{r.sender_nome || r.sender_id || "-"}</td>
                   <td className="p-2">
                     <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${
                       r.estado === "enviado" ? "bg-emerald-100 text-emerald-800" :
                       r.estado === "erro" ? "bg-red-100 text-red-800" :
                       r.estado === "bounced" ? "bg-amber-100 text-amber-800" :
                       "bg-slate-200 text-slate-700"
                     }`}>{r.estado || "?"}</span>
                   </td>
                   <td className="p-2 text-red-600 text-[11px] truncate max-w-xs" title={r.erro || ""}>{r.erro ? `${r.erro}` : ""}</td>
                   <td className="p-2 text-center">{(r.opened_at || []).length > 0 ? <span className="text-emerald-600 font-bold">OK</span> : ""}</td>
                   <td className="p-2 text-center">{(r.clicked_at || []).length > 0 ? <span className="text-blue font-bold">OK</span> : ""}</td>
                 </tr>
               ))}
             </tbody>
           </table>
         </div>}
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Log de eventos SendGrid (webhooks)
// -----------------------------------------------------------------------------
function WebhookLogSection() {
  const [rows, setRows] = useState([]);
  const [senders, setSenders] = useState([]);
  const [filters, setFilters] = useState({ sender_id: "", event: "", matched: "", limit: 200 });
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v !== "" && v !== undefined) params.append(k, v); });
      const { data } = await api.get(`/marketing/webhook/events?${params.toString()}`);
      setRows(data || []);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [filters]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/marketing/senders").then(({ data }) => setSenders(data || [])).catch(() => {});
  }, []);

  const openDetail = async (eid) => {
    try {
      const { data } = await api.get(`/marketing/webhook/events/${eid}`);
      setDetail(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-3">
      <div className="bg-slate-50 border border-tint rounded-md p-3 text-[12px] text-navy">
        Registo cru dos eventos recebidos do SendGrid por cada remetente. Clique numa linha para ver o payload completo (debug). Util para diagnosticar porque opens/clicks nao estao a chegar.
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <select value={filters.sender_id} onChange={(e) => setFilters({ ...filters, sender_id: e.target.value })}
          data-testid="em-wh-filter-sender" className="text-[12px] border border-tint rounded px-2 py-1 bg-white">
          <option value="">Todos os remetentes</option>
          {senders.map((s) => <option key={s.id} value={s.id}>{s.nome}</option>)}
        </select>
        <select value={filters.event} onChange={(e) => setFilters({ ...filters, event: e.target.value })}
          data-testid="em-wh-filter-event" className="text-[12px] border border-tint rounded px-2 py-1 bg-white">
          <option value="">Qualquer evento</option>
          {Object.keys(EVENT_BADGE).map((k) => <option key={k} value={k}>{k}</option>)}
        </select>
        <select value={filters.matched} onChange={(e) => setFilters({ ...filters, matched: e.target.value })}
          data-testid="em-wh-filter-matched" className="text-[12px] border border-tint rounded px-2 py-1 bg-white">
          <option value="">Qualquer match</option>
          <option value="true">Associado a send</option>
          <option value="false">NAO associado</option>
        </select>
        <select value={filters.limit} onChange={(e) => setFilters({ ...filters, limit: Number(e.target.value) })}
          data-testid="em-wh-filter-limit" className="text-[12px] border border-tint rounded px-2 py-1 bg-white">
          {[100, 200, 500, 1000].map((n) => <option key={n} value={n}>Ultimos {n}</option>)}
        </select>
        <button onClick={load} data-testid="em-wh-refresh"
          className="text-[12px] text-blue hover:underline inline-flex items-center gap-1">
          <RefreshCw size={11} /> Actualizar
        </button>
        <div className="ml-auto text-[11px] text-muted2">{rows.length} registos</div>
      </div>

      <div className="border border-tint rounded-lg bg-white overflow-hidden">
        {loading ? <div className="text-center py-6 text-muted2">A carregar...</div> :
         rows.length === 0 ? <div className="text-center py-8 text-muted2 italic">Nao foram recebidos eventos SendGrid ainda. Verifique se configurou o Event Webhook em SendGrid para cada remetente.</div> :
         <div className="overflow-x-auto">
           <table className="w-full text-[12px]" data-testid="em-wh-table">
             <thead className="text-[10px] uppercase text-slate-500 border-b border-tint bg-slate-50">
               <tr>
                 <th className="text-left p-2">Recebido</th>
                 <th className="text-left p-2 w-20">Evento</th>
                 <th className="text-left p-2">Email</th>
                 <th className="text-left p-2">Remetente</th>
                 <th className="text-left p-2">Campanha</th>
                 <th className="text-left p-2 w-20">Match?</th>
                 <th className="text-left p-2">Nota</th>
               </tr>
             </thead>
             <tbody>
               {rows.map((r) => (
                 <tr key={r.id} className="border-b border-tint last:border-b-0 hover:bg-slate-50 cursor-pointer"
                   onClick={() => openDetail(r.id)} data-testid={`em-wh-row-${r.id}`}>
                   <td className="p-2 text-[11px] text-muted2">{fmtDT(r.recebido_em, true)}</td>
                   <td className="p-2">
                     <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${EVENT_BADGE[r.event] || "bg-slate-100 text-slate-700"}`}>{r.event}</span>
                   </td>
                   <td className="p-2 truncate max-w-xs">{r.email || "-"}</td>
                   <td className="p-2 text-[11px]">{(senders.find((s) => s.id === r.sender_id) || {}).nome || r.sender_id || "-"}</td>
                   <td className="p-2 text-[11px] truncate max-w-xs">{r.campaign_id || "-"}</td>
                   <td className="p-2">
                     <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${r.matched ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-800"}`}>
                       {r.matched ? "SIM" : "NAO"}
                     </span>
                   </td>
                   <td className="p-2 text-[11px] text-muted2 truncate max-w-xs" title={r.note}>{r.note}</td>
                 </tr>
               ))}
             </tbody>
           </table>
         </div>}
      </div>

      <Modal open={!!detail} onClose={() => setDetail(null)} title="Payload completo do evento SendGrid" testid="em-wh-detail-modal" size="xl">
        {detail && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2 text-[12px]">
              <div><b>Evento:</b> {detail.event}</div>
              <div><b>Recebido:</b> {detail.recebido_em}</div>
              <div><b>Email:</b> {detail.email || "-"}</div>
              <div><b>Send ID:</b> <code className="text-[11px]">{detail.send_id || "-"}</code></div>
              <div><b>Campaign:</b> <code className="text-[11px]">{detail.campaign_id || "-"}</code></div>
              <div><b>Match:</b> {detail.matched ? "SIM" : "NAO"}</div>
            </div>
            {detail.note && <div className="text-amber-700 text-[12px]"><AlertTriangle size={12} className="inline" /> {detail.note}</div>}
            <div>
              <div className="text-[11px] font-semibold text-muted2 uppercase mb-1">Payload raw</div>
              <pre className="bg-slate-100 border border-tint rounded p-2 text-[11px] font-mono overflow-auto max-h-96">{JSON.stringify(detail.payload, null, 2)}</pre>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Filtro de bots/scanners de seguranca (Microsoft Defender, Barracuda, Proofpoint, etc)
// -----------------------------------------------------------------------------
function BotFilterSection() {
  const [cfg, setCfg] = useState(null);
  const [ipsText, setIpsText] = useState("");
  const [uaText, setUaText] = useState("");
  const [saving, setSaving] = useState(false);
  const [reprocessing, setReprocessing] = useState(false);
  const [sendingDigest, setSendingDigest] = useState(false);
  const [sendingUnsubReport, setSendingUnsubReport] = useState(false);
  const [refreshingMsFeed, setRefreshingMsFeed] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/marketing/bot-filter");
      setCfg(data);
      setIpsText((data.ips || []).join("\n"));
      setUaText((data.ua_keywords || []).join("\n"));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/marketing/bot-filter", { ativo: !!cfg.ativo, ips: ipsText, ua_keywords: uaText });
      toast.success("Filtro de bots guardado.");
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const reprocessar = async () => {
    setReprocessing(true);
    try {
      const { data } = await api.post("/marketing/bot-filter/reprocess");
      toast.success(
        `Reprocessado: ${data.analisados} eventos (${data.corrigidos} corrigidos). ` +
        `Unsubscribes: ${data.unsubs_analisados} analisados, ${data.unsubs_corrigidos} revertidos.`
      );
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setReprocessing(false); }
  };

  const restaurarDefaults = () => {
    setIpsText((cfg.default_ips || []).join("\n"));
    setUaText((cfg.default_ua_keywords || []).join("\n"));
  };

  const enviarDigestAgora = async () => {
    setSendingDigest(true);
    try {
      const { data } = await api.post("/marketing/bot-filter/digest/run", { force: true });
      if (data.enviado) {
        toast.success(`Resumo enviado: ${data.n_ips} IP(s) novo(s). Email: ${data.email_entregue ? "OK" : "falhou/nao configurado"}, Telegram: ${data.telegram_entregue ? "OK" : "falhou/nao configurado"}.`);
      } else {
        toast.info(data.motivo === "sem_novidades" ? "Sem IPs novos pendentes para reportar." : "Resumo ja enviado hoje.");
      }
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSendingDigest(false); }
  };

  const enviarUnsubReportAgora = async () => {
    setSendingUnsubReport(true);
    try {
      const { data } = await api.post("/marketing/bot-filter/unsub-report/run", { force: true });
      toast.success(`Relatorio enviado: ${data.total} unsubscribe(s) evitado(s) nos ultimos 7 dias. Email: ${data.email_entregue ? "OK" : "falhou/nao configurado"}, Telegram: ${data.telegram_entregue ? "OK" : "falhou/nao configurado"}.`);
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSendingUnsubReport(false); }
  };

  const refreshMsFeed = async () => {
    setRefreshingMsFeed(true);
    try {
      const { data } = await api.post("/marketing/bot-filter/ms-feed/refresh");
      if (data.atualizado) {
        toast.success(`Lista da Microsoft actualizada: ${data.total_ips} blocos de IP carregados.`);
      } else {
        toast.error(data.erro ? `Falha ao actualizar: ${data.erro}` : "Nao foi possivel actualizar agora.");
      }
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setRefreshingMsFeed(false); }
  };

  const toggleMsFeed = async (ativo) => {
    try {
      await api.put("/marketing/bot-filter/ms-feed", { ativo });
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (!cfg) return <div className="text-center py-6 text-muted2">A carregar...</div>;

  return (
    <div className="max-w-3xl space-y-4">
      <div className="bg-slate-50 border border-tint rounded-md p-3 text-[12px] text-navy flex items-start gap-2">
        <ShieldAlert size={14} className="mt-0.5 text-blue flex-shrink-0" />
        <div>
          Scanners de seguranca corporativos (Microsoft Defender, Barracuda, Proofpoint, Mimecast, Checkpoint, etc)
          pre-visualizam links e imagens no momento da entrega, gerando aberturas/cliques que nao sao do destinatario real.
          Os eventos abaixo sao ignorados automaticamente nas estatisticas de campanhas.
        </div>
      </div>

      <div className="border border-tint rounded-lg p-4 bg-white space-y-4">
        <label className="inline-flex items-center gap-2 text-sm font-semibold text-navy">
          <input type="checkbox" checked={!!cfg.ativo} onChange={(e) => setCfg({ ...cfg, ativo: e.target.checked })} data-testid="bf-ativo" />
          Filtro de bots/scanners activo
        </label>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold text-navy mb-1">IPs / ranges (CIDR) bloqueados</label>
            <div className="text-[11px] text-muted2 mb-1">Um por linha. Suporta IP exacto (ex: 48.209.223.92) ou range CIDR (ex: 3.145.232.16/28).</div>
            <textarea value={ipsText} onChange={(e) => setIpsText(e.target.value)} rows={8} data-testid="bf-ips"
              className="w-full px-3 py-2 border border-tint rounded text-[12px] font-mono" />
          </div>
          <div>
            <label className="block text-sm font-semibold text-navy mb-1">Palavras-chave do User-Agent bloqueadas</label>
            <div className="text-[11px] text-muted2 mb-1">Uma por linha. Comparacao parcial, sem distinguir maiusculas/minusculas.</div>
            <textarea value={uaText} onChange={(e) => setUaText(e.target.value)} rows={8} data-testid="bf-ua"
              className="w-full px-3 py-2 border border-tint rounded text-[12px] font-mono" />
          </div>
        </div>

        <div className="flex items-center justify-between pt-2 border-t border-tint">
          <button onClick={restaurarDefaults} data-testid="bf-restore-defaults" className="text-[12px] text-blue hover:underline">
            Restaurar listas predefinidas
          </button>
          <Btn onClick={save} disabled={saving} testid="bf-save"><Save size={13} /> Guardar</Btn>
        </div>
      </div>

      <div className="border border-blue-200 bg-blue-50 rounded-lg p-4 space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="text-sm font-semibold text-navy flex items-center gap-2">
              Lista Microsoft Exchange Online / EOP
              <label className="inline-flex items-center gap-1 text-[11px] font-normal text-muted2 ml-2">
                <input type="checkbox" checked={cfg.ms_feed?.ativo !== false}
                  onChange={(e) => toggleMsFeed(e.target.checked)} data-testid="bf-ms-feed-ativo" />
                activo
              </label>
            </div>
            <div className="text-[11px] text-muted2">
              Actualizada automaticamente 1x/semana a partir do endpoint publico oficial da Microsoft (sem chave de API),
              cobrindo os blocos de IP do Exchange Online/EOP responsaveis por Safe Links e scanning automatico de emails.
              {" "}Blocos carregados: <b data-testid="bf-ms-feed-total">{cfg.ms_feed?.total_ips || 0}</b>
              {cfg.ms_feed?.atualizado_em && <> - actualizado em {fmtDT(cfg.ms_feed.atualizado_em, true)}</>}
              {cfg.ms_feed?.erro && <span className="text-red-600"> - erro na ultima actualizacao: {cfg.ms_feed.erro}</span>}
            </div>
          </div>
          <Btn onClick={refreshMsFeed} disabled={refreshingMsFeed} variant="ghost" testid="bf-ms-feed-refresh">
            <RefreshCw size={13} /> {refreshingMsFeed ? "A actualizar..." : "Actualizar agora"}
          </Btn>
        </div>
      </div>

      <IpProvidersSection />

      <div className="border border-tint rounded-lg p-4 bg-white space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm font-semibold text-navy">Reprocessar eventos existentes</div>
            <div className="text-[11px] text-muted2">
              Revarre eventos de open/click ja registados e corrige estatisticas de envios inflacionadas por scanners detectados pela lista acima.
            </div>
          </div>
          <Btn onClick={reprocessar} disabled={reprocessing} variant="ghost" testid="bf-reprocess">
            <RefreshCw size={13} /> {reprocessing ? "A reprocessar..." : "Reprocessar agora"}
          </Btn>
        </div>
        <div className="text-[12px] text-navy">
          Total de eventos ja filtrados como bot: <b data-testid="bf-total-filtrados">{cfg.total_filtrados}</b>
        </div>
      </div>

      <div className="border border-amber-200 bg-amber-50 rounded-lg p-4 space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="text-sm font-semibold text-navy">Alertas de novos IPs de scanner</div>
            <div className="text-[11px] text-muted2">
              Resumo diario (email + Telegram, usando as configuracoes ja existentes) com os IPs de scanner nunca antes vistos, para decidir se os adiciona a lista negra.
              IPs novos pendentes de resumo: <b data-testid="bf-ips-pendentes">{cfg.ips_novos_pendentes || 0}</b>
            </div>
          </div>
          <Btn onClick={enviarDigestAgora} disabled={sendingDigest} variant="ghost" testid="bf-digest-run">
            <Send size={13} /> {sendingDigest ? "A enviar..." : "Enviar resumo agora"}
          </Btn>
        </div>
        {cfg.ultimo_digest && (
          <div className="text-[11px] text-muted2 border-t border-amber-200 pt-2" data-testid="bf-ultimo-digest">
            Ultimo resumo: {fmtDT(cfg.ultimo_digest.enviado_em, true)} - {cfg.ultimo_digest.n_ips} IP(s) -
            {" "}Email: {cfg.ultimo_digest.email_entregue ? "entregue" : "nao entregue"}, Telegram: {cfg.ultimo_digest.telegram_entregue ? "entregue" : "nao entregue"}
          </div>
        )}
      </div>

      <div className="border border-amber-200 bg-amber-50 rounded-lg p-4 space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="text-sm font-semibold text-navy">Relatorio semanal de unsubscribes evitados</div>
            <div className="text-[11px] text-muted2">
              Todas as 6as feiras (email + Telegram): total de cancelamentos de subscricao causados por scanners, bloqueados antes de aplicar ou revertidos automaticamente, com os IPs/motivos mais frequentes.
            </div>
          </div>
          <Btn onClick={enviarUnsubReportAgora} disabled={sendingUnsubReport} variant="ghost" testid="bf-unsub-report-run">
            <Send size={13} /> {sendingUnsubReport ? "A enviar..." : "Enviar relatorio agora"}
          </Btn>
        </div>
        {cfg.ultimo_unsub_report && (
          <div className="text-[11px] text-muted2 border-t border-amber-200 pt-2" data-testid="bf-ultimo-unsub-report">
            Ultimo relatorio: {fmtDT(cfg.ultimo_unsub_report.enviado_em, true)} - {cfg.ultimo_unsub_report.total} unsubscribe(s) evitado(s) -
            {" "}Email: {cfg.ultimo_unsub_report.email_entregue ? "entregue" : "nao entregue"}, Telegram: {cfg.ultimo_unsub_report.telegram_entregue ? "entregue" : "nao entregue"}
          </div>
        )}
      </div>

      <div className="border border-tint rounded-lg bg-white overflow-hidden">
        <div className="p-3 border-b border-tint text-sm font-semibold text-navy">Eventos filtrados recentemente</div>
        {(cfg.recentes || []).length === 0 ? (
          <div className="text-center py-8 text-muted2 italic">Nenhum evento de scanner detectado ainda.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[12px]" data-testid="bf-recentes-table">
              <thead className="text-[10px] uppercase text-slate-500 border-b border-tint bg-slate-50">
                <tr>
                  <th className="text-left p-2">Recebido</th>
                  <th className="text-left p-2 w-20">Evento</th>
                  <th className="text-left p-2">Email</th>
                  <th className="text-left p-2">IP</th>
                  <th className="text-left p-2">Motivo</th>
                </tr>
              </thead>
              <tbody>
                {cfg.recentes.map((r) => (
                  <tr key={r.id} className="border-b border-tint last:border-b-0" data-testid={`bf-recente-${r.id}`}>
                    <td className="p-2 text-[11px] text-muted2">{fmtDT(r.recebido_em, true)}</td>
                    <td className="p-2">
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${EVENT_BADGE[r.event] || "bg-slate-100 text-slate-700"}`}>{r.event}</span>
                    </td>
                    <td className="p-2 truncate max-w-xs">{r.email || "-"}</td>
                    <td className="p-2 font-mono text-[11px]">{r.ip || "-"}</td>
                    <td className="p-2 text-[11px] text-muted2">{r.note}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Fornecedores de IPs de scanners/bots (Microsoft Defender, Google Workspace,
// Proofpoint, Barracuda, Mimecast, Cisco IronPort, etc) - extensivel pelo admin
// -----------------------------------------------------------------------------
function IpProvidersSection() {
  const [providers, setProviders] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [editText, setEditText] = useState("");
  const [savingId, setSavingId] = useState(null);
  const [refreshingId, setRefreshingId] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [newProvider, setNewProvider] = useState({ nome: "", modo: "manual", ips: "", source_url: "", parser: "plain_json_array" });
  const [creating, setCreating] = useState(false);
  const [candidates, setCandidates] = useState(null);
  const [dismissingId, setDismissingId] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/marketing/bot-filter/ip-providers");
      setProviders(data || []);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  }, []);

  const loadCandidates = useCallback(async () => {
    try {
      const { data } = await api.get("/marketing/bot-filter/provider-candidates?min_hits=3");
      setCandidates(data.candidates || []);
    } catch { setCandidates([]); }
  }, []);

  useEffect(() => { load(); loadCandidates(); }, [load, loadCandidates]);

  const toggleAtivo = async (p) => {
    try {
      await api.put(`/marketing/bot-filter/ip-providers/${p.id}`, { ativo: !p.ativo });
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const openEdit = (p) => {
    setExpanded(expanded === p.id ? null : p.id);
    setEditText((p.ips || []).join("\n"));
  };

  const saveIps = async (p) => {
    setSavingId(p.id);
    try {
      await api.put(`/marketing/bot-filter/ip-providers/${p.id}`, { ips: editText });
      toast.success(`${p.nome} actualizado.`);
      setExpanded(null);
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSavingId(null); }
  };

  const refreshUrl = async (p) => {
    setRefreshingId(p.id);
    try {
      const { data } = await api.post(`/marketing/bot-filter/ip-providers/${p.id}/refresh`);
      if (data.ok) toast.success(`${p.nome}: ${data.total_ips} blocos carregados.`);
      else toast.error(data.erro || "Falha ao actualizar.");
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setRefreshingId(null); }
  };

  const removeProvider = async (p) => {
    if (!window.confirm(`Remover o fornecedor "${p.nome}"?`)) return;
    try {
      await api.delete(`/marketing/bot-filter/ip-providers/${p.id}`);
      toast.success("Fornecedor removido.");
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const createProvider = async () => {
    if (!newProvider.nome.trim()) { toast.error("Indique um nome."); return; }
    setCreating(true);
    try {
      await api.post("/marketing/bot-filter/ip-providers", newProvider);
      toast.success("Fornecedor adicionado.");
      setShowAdd(false);
      setNewProvider({ nome: "", modo: "manual", ips: "", source_url: "", parser: "plain_json_array" });
      await load();
      await loadCandidates();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setCreating(false); }
  };

  const acceptCandidate = (c) => {
    setNewProvider({ nome: c.nome_sugerido, modo: "manual", ips: c.cidr_sugerido, source_url: "", parser: "plain_json_array" });
    setShowAdd(true);
  };

  const dismissCandidate = async (c) => {
    setDismissingId(c.sugestao_id);
    try {
      await api.post("/marketing/bot-filter/provider-candidates/dismiss", { ips: c.ips_membros });
      toast.success("Sugestao dispensada.");
      await loadCandidates();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setDismissingId(null); }
  };

  if (!providers) return <div className="text-center py-6 text-muted2">A carregar...</div>;

  return (
    <div className="border border-tint rounded-lg bg-white overflow-hidden" data-testid="ip-providers-section">
      <div className="p-4 border-b border-tint flex items-center justify-between">
        <div>
          <div className="text-sm font-semibold text-navy flex items-center gap-2">
            Fornecedores de IPs de scanners/bots
            {candidates && candidates.length > 0 && (
              <span className="inline-flex items-center gap-1 text-[10px] font-bold bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full" data-testid="candidates-badge">
                <Lightbulb size={10} /> {candidates.length} sugestao{candidates.length !== 1 ? "es" : ""}
              </span>
            )}
          </div>
          <div className="text-[11px] text-muted2">
            Microsoft Defender, Google Workspace, Proofpoint, Barracuda, Mimecast, Cisco IronPort e outros - geridos manualmente ou por URL publica.
          </div>
        </div>
        <Btn onClick={() => setShowAdd((v) => !v)} variant="ghost" testid="ip-provider-add-toggle">
          <Plus size={13} /> Adicionar fornecedor
        </Btn>
      </div>

      {showAdd && (
        <div className="p-4 border-b border-tint bg-slate-50 space-y-3" data-testid="ip-provider-add-form">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] font-semibold text-navy mb-1">Nome</label>
              <input value={newProvider.nome} onChange={(e) => setNewProvider({ ...newProvider, nome: e.target.value })}
                className="w-full px-3 py-2 border border-tint rounded text-sm" data-testid="ip-provider-nome-input" />
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-navy mb-1">Modo</label>
              <select value={newProvider.modo} onChange={(e) => setNewProvider({ ...newProvider, modo: e.target.value })}
                className="w-full px-3 py-2 border border-tint rounded text-sm" data-testid="ip-provider-modo-select">
                <option value="manual">Manual (colar lista de IPs/CIDR)</option>
                <option value="url">URL publica (busca automatica)</option>
              </select>
            </div>
          </div>
          {newProvider.modo === "manual" ? (
            <div>
              <label className="block text-[11px] font-semibold text-navy mb-1">IPs / CIDR (um por linha)</label>
              <textarea value={newProvider.ips} onChange={(e) => setNewProvider({ ...newProvider, ips: e.target.value })}
                rows={4} className="w-full px-3 py-2 border border-tint rounded text-[12px] font-mono" data-testid="ip-provider-ips-textarea" />
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[11px] font-semibold text-navy mb-1">URL publica (JSON)</label>
                <input value={newProvider.source_url} onChange={(e) => setNewProvider({ ...newProvider, source_url: e.target.value })}
                  placeholder="https://..." className="w-full px-3 py-2 border border-tint rounded text-sm" data-testid="ip-provider-url-input" />
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-navy mb-1">Formato da resposta</label>
                <select value={newProvider.parser} onChange={(e) => setNewProvider({ ...newProvider, parser: e.target.value })}
                  className="w-full px-3 py-2 border border-tint rounded text-sm" data-testid="ip-provider-parser-select">
                  <option value="plain_json_array">Lista simples de IPs/CIDR (array JSON)</option>
                  <option value="google_json">Formato Google (prefixes: ipv4Prefix/ipv6Prefix)</option>
                </select>
              </div>
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Btn onClick={() => setShowAdd(false)} variant="ghost" testid="ip-provider-cancel-btn">Cancelar</Btn>
            <Btn onClick={createProvider} disabled={creating} testid="ip-provider-create-btn">
              {creating ? "A adicionar..." : "Adicionar"}
            </Btn>
          </div>
        </div>
      )}

      {candidates && candidates.length > 0 && (
        <div className="p-4 border-b border-amber-200 bg-amber-50 space-y-3" data-testid="provider-candidates-section">
          <div className="flex items-center gap-2">
            <Lightbulb size={15} className="text-amber-600 flex-shrink-0" />
            <div>
              <div className="text-sm font-semibold text-amber-900">
                {candidates.length} sugestao{candidates.length !== 1 ? "es" : ""} de novo fornecedor
              </div>
              <div className="text-[11px] text-amber-700">
                IPs detectados repetidamente que nao pertencem a nenhum fornecedor nomeado. Crie um fornecedor dedicado para facilitar a gestao.
              </div>
            </div>
          </div>
          <div className="space-y-2">
            {candidates.map((c) => (
              <div key={c.sugestao_id} className="bg-white border border-amber-200 rounded-md p-3 flex items-start justify-between gap-3" data-testid={`candidate-row-${c.sugestao_id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-sm font-semibold text-navy">{c.cidr_sugerido}</span>
                    {c.is_grouped && (
                      <span className="text-[10px] bg-amber-100 text-amber-700 rounded-full px-2 py-0.5 font-semibold">
                        {c.ips_membros.length} IPs agrupados no /24
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-muted2 mt-0.5">
                    {c.hit_count_total} deteccao{c.hit_count_total !== 1 ? "es" : ""} - visto desde {fmtDT(c.first_seen, true)}
                    {c.motivos?.length > 0 && <span className="ml-1 text-slate-500">({c.motivos.slice(0, 2).join("; ")})</span>}
                  </div>
                  {c.is_grouped && (
                    <div className="text-[10px] text-muted2 mt-0.5 font-mono">
                      {c.ips_membros.join(", ")}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Btn onClick={() => acceptCandidate(c)} variant="ghost" testid={`candidate-create-${c.sugestao_id}`}>
                    <Plus size={13} /> Criar fornecedor
                  </Btn>
                  <Btn
                    onClick={() => dismissCandidate(c)}
                    disabled={dismissingId === c.sugestao_id}
                    variant="ghost"
                    testid={`candidate-dismiss-${c.sugestao_id}`}
                  >
                    <XCircle size={13} /> {dismissingId === c.sugestao_id ? "..." : "Dispensar"}
                  </Btn>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {providers.length === 0 ? (
        <div className="text-center py-8 text-muted2 italic">Sem fornecedores configurados.</div>
      ) : (
        <div>
          {providers.map((p) => (
            <div key={p.id} className="border-b border-tint last:border-b-0" data-testid={`ip-provider-row-${p.id}`}>
              <div className="flex items-center justify-between p-3">
                <div className="flex items-center gap-3">
                  <input type="checkbox" checked={!!p.ativo} onChange={() => toggleAtivo(p)} data-testid={`ip-provider-ativo-${p.id}`} />
                  <div>
                    <div className="text-sm font-semibold text-navy">{p.nome}</div>
                    <div className="text-[11px] text-muted2">
                      {p.modo === "url" ? "URL automatica" : "Manual"} - {p.total_ips} bloco(s)
                      {p.atualizado_em && <> - actualizado em {fmtDT(p.atualizado_em, true)}</>}
                      {p.erro && <span className="text-red-600"> - erro: {p.erro}</span>}
                      {!p.is_builtin && <span className="text-blue-600"> - personalizado</span>}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {p.modo === "manual" ? (
                    <Btn onClick={() => openEdit(p)} variant="ghost" testid={`ip-provider-edit-${p.id}`}>
                      <Edit3 size={13} /> Editar lista
                    </Btn>
                  ) : (
                    <Btn onClick={() => refreshUrl(p)} disabled={refreshingId === p.id} variant="ghost" testid={`ip-provider-refresh-${p.id}`}>
                      <RefreshCw size={13} /> {refreshingId === p.id ? "A actualizar..." : "Actualizar agora"}
                    </Btn>
                  )}
                  {!p.is_builtin && (
                    <Btn onClick={() => removeProvider(p)} variant="ghost" testid={`ip-provider-delete-${p.id}`}>
                      <Trash2 size={13} />
                    </Btn>
                  )}
                </div>
              </div>
              {expanded === p.id && (
                <div className="p-3 pt-0 space-y-2">
                  <textarea value={editText} onChange={(e) => setEditText(e.target.value)} rows={6}
                    className="w-full px-3 py-2 border border-tint rounded text-[12px] font-mono" data-testid={`ip-provider-textarea-${p.id}`} />
                  <div className="flex justify-end">
                    <Btn onClick={() => saveIps(p)} disabled={savingId === p.id} testid={`ip-provider-save-${p.id}`}>
                      <Save size={13} /> {savingId === p.id ? "A guardar..." : "Guardar"}
                    </Btn>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Autenticacao de dominio (SPF/DKIM/DMARC) - usado pelo botao "Verificar DNS"
// -----------------------------------------------------------------------------
function DnsStatusBadge({ status }) {
  const map = {
    ok: { bg: "bg-emerald-100 text-emerald-700", label: "OK", Icon: CheckCircle2 },
    warn: { bg: "bg-amber-100 text-amber-700", label: "Aviso", Icon: AlertTriangle },
    missing: { bg: "bg-red-100 text-red-700", label: "Em falta", Icon: XCircle },
    error: { bg: "bg-slate-200 text-slate-600", label: "Erro", Icon: AlertTriangle },
  };
  const it = map[status] || map.error;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded ${it.bg}`}>
      <it.Icon size={11} /> {it.label}
    </span>
  );
}

function DomainAuthPanel({ state, onRefresh }) {
  if (state.loading || !state.data) {
    return <div className="text-center text-muted2 py-8">A consultar DNS...</div>;
  }
  const d = state.data;
  const summary = d.summary;
  const summaryMap = {
    healthy: { bg: "bg-emerald-50 border-emerald-200", txt: "text-emerald-700", Icon: ShieldCheck, label: "Autenticacao completa" },
    warning: { bg: "bg-amber-50 border-amber-200", txt: "text-amber-700", Icon: AlertTriangle, label: "A configurar - avisos" },
    critical: { bg: "bg-red-50 border-red-200", txt: "text-red-700", Icon: ShieldAlert, label: "Autenticacao incompleta - risco de deliverability" },
  };
  const sm = summaryMap[summary] || summaryMap.critical;
  const Row = ({ title, res }) => (
    <div className="border border-tint rounded-lg p-3 bg-white">
      <div className="flex items-center justify-between mb-1">
        <span className="font-semibold text-navy">{title}</span>
        <DnsStatusBadge status={res?.status} />
      </div>
      {res?.value && <div className="text-[11px] font-mono bg-slate-50 rounded p-2 mb-1 break-all">{res.value}</div>}
      {res?.selectors && (
        <ul className="text-[11px] space-y-0.5">
          {res.selectors.map((s) => (
            <li key={s.selector} className="flex items-center gap-2">
              <span className="font-mono text-muted2">{s.selector}._domainkey:</span>
              {s.found ? <span className="font-mono text-navy break-all">{s.target}</span> : <span className="text-red-600 italic">nao encontrado</span>}
            </li>
          ))}
        </ul>
      )}
      {res?.hint && <div className="text-[11px] text-muted2 mt-1">{res.hint}</div>}
    </div>
  );
  const wizard = d.wizard_records || [];
  const copy = (v) => { navigator.clipboard.writeText(v); toast.success("Copiado."); };
  const kindLabel = { spf: "SPF", dkim: "DKIM", dmarc: "DMARC" };
  const kindTone = { spf: "bg-blue text-white", dkim: "bg-emerald-600 text-white", dmarc: "bg-amber-600 text-white" };
  return (
    <div className="space-y-3" data-testid="dns-auth-panel">
      <div className="text-xs text-muted2">Remetente: <strong>{state.sender_nome}</strong> ({state.from_email}) - dominio consultado: <span className="font-mono">{d.domain || "-"}</span></div>
      <div className={`border ${sm.bg} rounded-lg p-3 flex items-start gap-2`}>
        <sm.Icon className={sm.txt} size={18} />
        <div className={`text-sm ${sm.txt} font-semibold`}>{sm.label}</div>
      </div>
      {d.error && <div className="text-sm text-red-600">{d.error}</div>}
      <Row title="SPF (v=spf1)" res={d.spf} />
      <Row title="DKIM (SendGrid s1/s2)" res={d.dkim} />
      <Row title="DMARC (_dmarc)" res={d.dmarc} />

      {wizard.length > 0 && (
        <div className="border border-blue-200 bg-blue-50 rounded-lg p-3" data-testid="dns-wizard">
          <div className="flex items-center gap-2 mb-2">
            <ShieldCheck size={14} className="text-blue" />
            <span className="text-sm font-bold text-navy">Assistente DNS - copie e cole no seu registrar</span>
          </div>
          <p className="text-[11px] text-muted2 mb-2">
            Adicione estes registos no painel do seu provedor DNS (Cloudflare, GoDaddy, Cognitum, etc). Para DKIM, os valores `uNNN.wlN` estao no dashboard SendGrid em Sender Authentication &gt; Domain Authentication.
          </p>
          <div className="space-y-2">
            {wizard.map((w, i) => (
              <div key={i} data-testid={`dns-wizard-${w.kind}-${i}`} className="bg-white border border-blue-100 rounded-md p-2.5">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <span className={`text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded ${kindTone[w.kind] || "bg-slate-600 text-white"}`}>
                    {kindLabel[w.kind] || w.kind} - {w.type}
                  </span>
                  <button data-testid={`dns-copy-${i}`} onClick={() => copy(w.value)}
                    className="inline-flex items-center gap-1 text-[11px] font-semibold text-navy hover:text-blue">
                    <Copy size={11} /> Copiar valor
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-[11px]">
                  <div>
                    <div className="text-muted2 uppercase tracking-wide text-[9px] font-semibold mb-0.5">Host</div>
                    <div className="font-mono break-all bg-slate-50 rounded p-1.5 text-navy">{w.host}</div>
                  </div>
                  <div className="md:col-span-2">
                    <div className="text-muted2 uppercase tracking-wide text-[9px] font-semibold mb-0.5">Valor</div>
                    <div className="font-mono break-all bg-slate-50 rounded p-1.5 text-navy">{w.value}</div>
                  </div>
                </div>
                {w.note && <div className="text-[10px] text-muted2 mt-1.5 italic">{w.note}</div>}
              </div>
            ))}
          </div>
          <div className="text-[10px] text-muted2 mt-2">Apos publicar, a propagacao DNS pode demorar 5-60 min. Volte aqui e clique "Reverificar" para confirmar.</div>
        </div>
      )}

      <div className="flex justify-end pt-1">
        <Btn variant="ghost" onClick={onRefresh} testid="dns-refresh"><Play size={12} /> Reverificar</Btn>
      </div>
    </div>
  );
}
